import React, { useCallback, useEffect, useRef, useState } from 'react'
import { CLASS_PREFIX } from 'src/constants'
import { DetailCard } from '../detail_card/detail_card'
import './project_feature_tabs.scss'

export type FeatureTab = {
  id: string;
  label: string;
  content: React.ReactNode;
};

type Props = {
  tabs: FeatureTab[];
};

const cls = `${CLASS_PREFIX}project-feature-tabs`

const ProjectFeatureTabs: React.FC<Props> = ({ tabs }) => {
  const [activeTabId, setActiveTabId] = useState(tabs[0]?.id)
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollIndicators = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    updateScrollIndicators()
    el.addEventListener('scroll', updateScrollIndicators)
    const observer = new ResizeObserver(updateScrollIndicators)
    observer.observe(el)
    return () => {
      el.removeEventListener('scroll', updateScrollIndicators)
      observer.disconnect()
    }
  }, [updateScrollIndicators])

  useEffect(() => {
    if (!tabs.some((tab) => tab.id === activeTabId)) {
      setActiveTabId(tabs[0]?.id)
    }
  }, [activeTabId, tabs])

  useEffect(() => {
    const strip = scrollRef.current
    const tab = activeTabId ? tabRefs.current[activeTabId] : null
    if (!strip || !tab) return
    // Scroll the strip itself, on one axis. scrollIntoView moves every scrollable
    // ancestor until the element is visible, and that includes the document.
    const stripBox = strip.getBoundingClientRect()
    const tabBox = tab.getBoundingClientRect()
    const overflowLeft = tabBox.left - stripBox.left
    const overflowRight = tabBox.right - stripBox.right
    // Round away from the edge: a scroll offset may be snapped to whole pixels, and
    // rounding the other way would leave a fraction of the tab outside.
    const delta = overflowLeft < 0
      ? Math.floor(overflowLeft)
      : overflowRight > 0 ? Math.ceil(overflowRight) : 0
    if (delta === 0) return
    // The oldest browsers this project supports have no scroll methods on elements;
    // assigning scrollLeft gets there too, without the animation.
    if (typeof strip.scrollBy === 'function') {
      strip.scrollBy({ left: delta, behavior: 'smooth' })
    } else {
      strip.scrollLeft += delta
    }
  }, [activeTabId])

  if (tabs.length === 0) {
    return null
  }

  const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? tabs[0]

  const tabsWrapperClass = [
    `${cls}__tabs-wrapper`,
    canScrollLeft ? `${cls}__tabs-wrapper--scroll-left` : '',
    canScrollRight ? `${cls}__tabs-wrapper--scroll-right` : ''
  ].filter(Boolean).join(' ')

  return (
    <DetailCard>
      <div className={cls}>
        <div className={tabsWrapperClass}>
          <div className={`${cls}__tabs`} ref={scrollRef}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                ref={(el) => { tabRefs.current[tab.id] = el }}
                className={`${cls}__tab-item ${tab.id === activeTab.id ? 'active' : ''}`}
                onClick={() => setActiveTabId(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        <div className={`${cls}__content`}>
          {activeTab.content}
        </div>
      </div>
    </DetailCard>
  )
}

export default ProjectFeatureTabs
